import React, { useEffect, useState } from 'react';
import { setBreadcrumbs } from 'works/legacy/lib/client-store';
import { useNavigate, useMutation, useQuery } from '@common/hooks';
import {
  ErrorHandler,
  FullScreenLoadingModal,
  PageSteps,
} from '@common/components';
import { ShiftsAddMultipleUpload } from './shifts-add-multiple-upload';
import { ShiftsAddMultipleConfirmModal } from './shifts-add-multiple-confirm-modal';
import { createMessage } from 'works/legacy/lib/message-helper';
import { RcFile } from 'antd/lib/upload';
import { Button } from 'antd';
import { ShiftsAddMultipleReview } from './shifs-add-multiple-review';
import {
  ValidateShiftsImport,
  ImportShifts,
  ImportShiftsProgressQuery,
  ImportShift,
  ImportShiftsProgressQueryResponse,
  ImportShiftsProgressQueryVariables,
} from '@scheduling/graphql';
import { CSVError } from 'works/legacy/lib/csv_error';

function SuccessMessage({ shiftsImported }: { shiftsImported: ImportShift[] }) {
  const isPlural = shiftsImported.length;

  function suffix() {
    const hasAddition = shiftsImported.some((shift) => !shift.isUpdate);
    const hasEdition = shiftsImported.some((shift) => shift.isUpdate);

    if (hasAddition && hasEdition) {
      return 'added or updated';
    }

    if (hasAddition) {
      return 'added';
    }

    return 'updated';
  }

  return (
    <>
      A total of {shiftsImported.length} shifts {isPlural ? 'were' : 'was'}{' '}
      {suffix()}
    </>
  );
}

export function ShiftsAddMultipleSteps() {
  useEffect(() => {
    setBreadcrumbs([
      {
        name: 'Shifts',
        path: '/works/shifts',
      },
      {
        name: 'Upload Shifts',
        path: '/works/shifts/feed/add-multiple',
      },
    ]);
  }, []);

  const navigate = useNavigate();
  const [csvFile, setCsvFile] = useState<RcFile | null>(null);
  const [errors, setErrors] = useState<CSVError[]>([]);
  const [importShifts, setImportShifts] = useState<ImportShift[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [shiftsImportId, setShiftsImportId] = useState<string>('');
  const [progressPending, setProgressPending] = useState(true);

  const errorMessage = () =>
    createMessage(
      'error',
      'Something went wrong, please contact support@workshealth.com for assistance.'
    );

  const [validateFile, { loading }] = useMutation(ValidateShiftsImport, {
    onCompleted: ({ validateShiftsImport }) => {
      if (!validateShiftsImport?.errors?.length) {
        setImportShifts(validateShiftsImport?.shifts || []);
      } else {
        setErrors(validateShiftsImport?.errors);
      }
    },
    onError: errorMessage,
  });

  const handleFileUpload = (file: RcFile) => {
    setImportShifts([]);
    setErrors([]);
    setCsvFile(file);

    validateFile({
      variables: {
        input: {
          file,
        },
      },
    });

    return false;
  };

  const {
    data,
    startPolling,
    stopPolling,
    error: queryError,
  } = useQuery<
    ImportShiftsProgressQueryResponse,
    ImportShiftsProgressQueryVariables
  >(ImportShiftsProgressQuery, {
    skip: !shiftsImportId,
    variables: { id: shiftsImportId },
  });

  useEffect(() => {
    const isPending = data?.shiftGapImport?.pending ?? true;
    setProgressPending(isPending);

    if (!isPending) {
      stopPolling();

      const state = data?.shiftGapImport?.state ?? 'failed';

      if (state === 'failed') {
        errorMessage();
      } else {
        createMessage(
          'success',
          <SuccessMessage shiftsImported={importShifts} />
        );
      }

      navigate('/works/shifts');
    }
  }, [data?.shiftGapImport, importShifts, stopPolling, navigate]);

  const [save] = useMutation(ImportShifts, {
    onCompleted({ createShiftGapImport }) {
      if (createShiftGapImport?.errors) {
        errorMessage();
        return;
      }

      const newShiftImportId = createShiftGapImport?.id;
      setShiftsImportId(newShiftImportId!);

      if (newShiftImportId) {
        startPolling(1000);
      }
    },
    onError: errorMessage,
  });

  if (queryError) {
    return <ErrorHandler error={queryError} log="ShiftsAddMultipleSteps" />;
  }

  const handleSave = () => {
    if (!csvFile) {
      errorMessage();
      return;
    }

    save({
      variables: {
        input: {
          file: csvFile,
        },
      },
    });
  };

  const openModal = () => setShowConfirmModal(true);

  const closeModal = () => setShowConfirmModal(false);

  return (
    <>
      <ShiftsAddMultipleConfirmModal
        visible={showConfirmModal}
        shiftsCount={importShifts.length}
        onCancel={closeModal}
        onConfirm={handleSave}
      />
      <FullScreenLoadingModal
        open={!!shiftsImportId && progressPending}
        description="Updates in progress, please do not refresh or close the page."
      />
      <PageSteps
        steps={[
          {
            title: 'Upload File',
            page: (
              <ShiftsAddMultipleUpload
                handleFileUpload={handleFileUpload}
                csvFile={csvFile}
              />
            ),
            forwardButtonDisabled: !importShifts.length && !errors.length,
            forwardButtonLoading: loading,
          },
          {
            title: 'Review File',
            page: (
              <ShiftsAddMultipleReview
                errors={errors}
                importShifts={importShifts}
              />
            ),
            customForwardAction() {
              if (errors.length) return <></>;

              return (
                <>
                  <Button onClick={openModal} size="large" type="primary">
                    Upload Shifts
                  </Button>
                </>
              );
            },
          },
        ]}
        onConfirm={() => {}}
      />
    </>
  );
}
